export interface AdminNavLink {
  label: string;
  icon: string;
  route?: string;
  badge?: number;
  permission?: string;
}

export interface AdminNavSection {
  title: string;
  links: AdminNavLink[];
}

const svg = (content: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="h-4 w-4" aria-hidden="true">${content}</svg>`;

const icons = {
  dashboard: svg(
    '<rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />',
  ),
  bookings: svg(
    '<path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke-linecap="round" /><rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 12h6M9 16h6" stroke-linecap="round" />',
  ),
  calendar: svg(
    '<rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" stroke-linecap="round" />',
  ),
  rooms: svg(
    '<path d="M3 12V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4M3 12v4h18v-4M7 12v-2h4v2M13 12v-2h4v2" stroke-linecap="round" stroke-linejoin="round" />',
  ),
  pricing: svg(
    '<path d="M7 7h.01M7 3h3c1.5 0 2.7 1.2 2.7 2.7 0 1.5-1.2 2.3-2.7 2.3H7V3Z" stroke-linecap="round" stroke-linejoin="round" /><path d="M7 7v14h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H7" stroke-linecap="round" stroke-linejoin="round" />',
  ),
  payments: svg(
    '<rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" stroke-linecap="round" />',
  ),
  guests: svg(
    '<circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" stroke-linecap="round" /><circle cx="17" cy="9" r="2" /><path d="M15 20a4 4 0 0 1 6 0" stroke-linecap="round" />',
  ),
  reviews: svg(
    '<path d="m12 3 2.2 4.5 5 .5-3.7 3.2 1.1 4.8L12 14.2 7.4 15.9l1.1-4.8L4.8 8l5-.5L12 3Z" stroke-linejoin="round" />',
  ),
  inquiries: svg(
    '<path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" stroke-linejoin="round" />',
  ),
  notifications: svg(
    '<path d="M15 17H9c-2.2 0-4-1.3-4-4V9c0-3.3 1.8-6 6-6s6 2.7 6 6v4c0 2.7-1.8 4-4 4Z" stroke-linecap="round" stroke-linejoin="round" /><path d="M10 19a2 2 0 0 0 4 0" stroke-linecap="round" />',
  ),
  content: svg(
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" stroke-linejoin="round" /><path d="M14 2v6h6M8 13h8M8 17h8" stroke-linecap="round" />',
  ),
  promotions: svg(
    '<path d="M12 3 9.5 8.5 3.5 9.3 8 13.7 6.8 20 12 16.8 17.2 20 16 13.7 20.5 9.3 14.5 8.5 12 3Z" stroke-linejoin="round" />',
  ),
  embed: svg(
    '<path d="m16 18 6-6-6-6M8 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round" />',
  ),
  analytics: svg(
    '<path d="M4 19V5M4 19h16" stroke-linecap="round" /><path d="M8 15V9M12 15V7M16 15v-4" stroke-linecap="round" />',
  ),
  users: svg(
    '<circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" stroke-linecap="round" /><path d="M16 11h5M18.5 8.5v5" stroke-linecap="round" />',
  ),
  settings: svg(
    '<circle cx="12" cy="12" r="3" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" stroke-linecap="round" />',
  ),
} as const;

export const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    title: 'Overview',
    links: [
      {
        label: 'Dashboard',
        icon: icons.dashboard,
        route: '/admin/dashboard',
        permission: 'Dashboard - Can view in sidebar',
      },
    ],
  },
  {
    title: 'Operations',
    links: [
      {
        label: 'Bookings',
        icon: icons.bookings,
        route: '/admin/bookings',
        permission: 'Bookings - Can view in sidebar',
      },
      {
        label: 'Calendar',
        icon: icons.calendar,
        route: '/admin/calendar',
        permission: 'Calendar - Can view in sidebar',
      },
      {
        label: 'Room types',
        icon: icons.rooms,
        route: '/admin/room-types',
        permission: 'Room types - Can view in sidebar',
      },
      {
        label: 'Pricing',
        icon: icons.pricing,
        route: '/admin/pricing',
        permission: 'Pricing - Can view in sidebar',
      },
      {
        label: 'Payments',
        icon: icons.payments,
        route: '/admin/payments',
        permission: 'Payments - Can view in sidebar',
      },
      {
        label: 'Guests',
        icon: icons.guests,
        route: '/admin/guests',
        permission: 'Guests - Can view in sidebar',
      },
    ],
  },
  {
    title: 'Guest experience',
    links: [
      {
        label: 'Reviews',
        icon: icons.reviews,
        route: '/admin/reviews',
        badge: 1,
        permission: 'Reviews - Can view in sidebar',
      },
      {
        label: 'Inquiries',
        icon: icons.inquiries,
        route: '/admin/inquiries',
        permission: 'Inquiries - Can view in sidebar',
      },
      {
        label: 'Notifications',
        icon: icons.notifications,
        route: '/admin/notifications',
        permission: 'Notifications - Can view in sidebar',
      },
    ],
  },
  {
    title: 'Marketing',
    links: [
      {
        label: 'Content',
        icon: icons.content,
        route: '/admin/content',
        permission: 'Content - Can view in sidebar',
      },
      {
        label: 'Promotions',
        icon: icons.promotions,
        route: '/admin/promotions',
        permission: 'Promotions - Can view in sidebar',
      },
      {
        label: 'Booking embed',
        icon: icons.embed,
        route: '/admin/booking-embed',
        permission: 'Booking embed - Can view in sidebar',
      },
    ],
  },
  {
    title: 'Insights',
    links: [
      {
        label: 'Analytics',
        icon: icons.analytics,
        route: '/admin/analytics',
        permission: 'Analytics - Can view in sidebar',
      },
    ],
  },
  {
    title: 'Workspace',
    links: [
      {
        label: 'Staff',
        icon: icons.users,
        route: '/admin/staff',
        permission: 'Staff - Can view in sidebar',
      },
      {
        label: 'Roles & permissions',
        icon: icons.settings,
        route: '/admin/roles',
        permission: 'Roles - Can view in sidebar',
      },
      {
        label: 'Settings',
        icon: icons.settings,
        route: '/admin/settings',
        permission: 'Settings - Can view in sidebar',
      },
    ],
  },
];
